import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword } from "../apis/resetPassword";
import { AccountLockedError, getApiErrorDetail } from "../utils/authErrors";
import { APP_ROUTES } from "../constants/appRoutes";
import AuthLayout from "./auth/AuthLayout";
import OtpInput from "./shared/OtpInput";
import Input from "./campaign/wizard/Input";
import "../App.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const emailFromQuery = searchParams.get("email") || "";

  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [warning, setWarning] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const successMessage =
    location.state?.message ||
    "If an account exists for this email, a password reset code has been sent.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWarning("");

    if (verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit verification code.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword({
        email: emailFromQuery,
        verification_code: verificationCode,
        new_password: newPassword,
      });
      toast.success("Password reset successfully. Please log in.");
      navigate(APP_ROUTES.HOME);
    } catch (err) {
      if (err instanceof AccountLockedError) {
        navigate("/account-locked");
        return;
      }
      const detail = getApiErrorDetail(err);
      if (detail.includes("expired")) {
        setWarning("Your verification code has expired. Please contact support.");
      } else if (detail.includes("attempt")) {
        setWarning(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!emailFromQuery) {
    return (
      <AuthLayout>
        <p className="mb-4 text-center text-sm text-slate-600">
          No email provided. Start from the forgot password page.
        </p>
        <Link className="btn btn-primary-app w-full font-semibold text-center" to="/forgot-password">
          Forgot password
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h1 className="mb-2 text-center text-xl font-bold text-slate-900">Reset password</h1>
      <p className="mb-6 text-center text-sm text-slate-600">{successMessage}</p>
      <p className="mb-4 text-center text-xs text-slate-500">
        Enter the 6-digit code from your email (expires in 10 minutes).
      </p>

      <form onSubmit={handleSubmit}>
        <Input
          label="Email address"
          labelIcon="envelope"
          type="email"
          name="email"
          value={emailFromQuery}
          readOnly
          wrapperClassName="mb-5"
        />

        <div className="mb-5">
          <p className="form-label mb-3 text-center text-xs font-bold uppercase tracking-[0.12em] text-dark/90">
            Verification code
          </p>
          <OtpInput
            value={verificationCode}
            onChange={setVerificationCode}
            disabled={submitting}
            autoFocus
          />
        </div>

        <Input
          label="New password"
          labelIcon="lock"
          type="password"
          name="new_password"
          autoComplete="new-password"
          placeholder="Enter new password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          wrapperClassName="mb-4"
        />

        <Input
          label="Confirm password"
          labelIcon="lock-fill"
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          wrapperClassName="mb-5"
        />

        {warning ? (
          <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {warning}
          </p>
        ) : null}

        <button
          type="submit"
          className="btn btn-primary-app w-full font-semibold"
          disabled={submitting}
        >
          {submitting ? "Resetting…" : "Reset password"}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-slate-600">
        <Link className="font-semibold text-slate-900 hover:text-slate-700" to={APP_ROUTES.HOME}>
          Back to home
        </Link>
      </p>
    </AuthLayout>
  );
};

export default ResetPassword;
